// ─────────────────────────────────────────────────────────────
// @ekkam/shared — Shared Constants
//
// Constants that must be identical on both the frontend (apps/web)
// and the backend (apps/api). Single source of truth — import from
// here instead of defining the same values in both places.
//
// Rules for adding a constant here:
//   ✅ Both FE and BE use it, and it must stay in sync
//   ❌ FE-only (timing, colours, pagination) → apps/web/src/lib/config.ts
//   ❌ BE-only (Redis, JWT, worker config)   → apps/api/src/config/constants.ts
// ─────────────────────────────────────────────────────────────

// ── Theme ──────────────────────────────────────────────────────
// Must stay in sync with:
//   - [data-theme='...'] selectors in apps/web/src/styles/globals.css
//   - schema.prisma @default("dark-purple")
export const VALID_THEMES = [
  'dark-purple', 'midnight', 'dark', 'light', 'solarized', 'rose', 'forest', 'ocean',
] as const

export type ThemeName = typeof VALID_THEMES[number]

export const DEFAULT_THEME: ThemeName = 'dark-purple'

// ── Profile limits ────────────────────────────────────────────
// Validated by the BE schema (users.schema.ts) and enforced in
// the FE character counters (SettingsPage).
export const MAX_DISPLAY_NAME_LEN = 50
export const MAX_BIO_LEN          = 300

// ── Discussion limits ─────────────────────────────────────────
// Validated by Zod on the BE (discussions.schema.ts) and used for
// real-time char-count feedback on the FE (CreateDiscussionPage).
export const DISCUSSION_TITLE_MIN   = 10
export const DISCUSSION_TITLE_MAX   = 300
export const DISCUSSION_CONTENT_MIN = 20
export const DISCUSSION_CONTENT_MAX = 50000

// ── Discussion taxonomy caps ──────────────────────────────────
// Enforced by Zod arrays on the BE and in the FE category/tag pickers.
export const MAX_DISCUSSION_TAGS       = 5
export const MAX_DISCUSSION_CATEGORIES = 3

// ── Moderation ────────────────────────────────────────────────
// The backend validates submitted reasons against this list.
// The frontend renders them in the report dialog.
// Admins can override via Redis (PUT /admin/reports/reasons) —
// that runtime override takes precedence over this default list.
export const REPORT_REASONS = [
  'Spam or misleading',
  'Harassment or hate speech',
  'Misinformation',
  'Inappropriate content',
  'Off-topic',
  'Other',
] as const

export type ReportReason = typeof REPORT_REASONS[number]

// ── Agent names ───────────────────────────────────────────────
// An agent is summoned by writing @Name in a comment, and extractMentions()
// in comments.service.ts reads that name back with /@([A-Za-z]\w*)/ — which
// stops dead at the first character outside [A-Za-z0-9_]. A name holding a
// `&`, a space or punctuation therefore can never be matched to its agent:
// "@Atta&Alchemy" parses as "Atta", resolves to nothing, and summons nobody.
// Restricting the name to what the parser can actually read keeps the two in
// step — change this pattern and that regex together, or mentions break again.
export const AGENT_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/

export const AGENT_NAME_RULE =
  'Agent name must start with a letter and contain only letters, numbers and underscores — no spaces or symbols, so it can be @mentioned.'
